import { useState, useRef, useEffect, useCallback, type KeyboardEvent } from 'react';
import { Panel } from '@xyflow/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import {
  Save,
  FolderOpen,
  Download,
  ChevronDown,
  FilePlus2,
  SaveAll,
  Pencil,
  X,
  Plus,
  Tag as TagIcon,
  Check,
  Loader2,
} from 'lucide-react';
import type { Tag } from '@/lib/tagsApi';

interface WorkflowTag {
  id: string;
  name: string;
  color?: string;
}

interface WorkflowActionsProps {
  onSave: () => void;
  onSaveAs: () => void;
  onNew: () => void;
  onLoad: () => void;
  onExport: () => void;
  // Workflow info
  workflowName?: string;
  workflowId?: string;
  isSaving?: boolean;
  hasUnsavedChanges?: boolean;
  lastSaved?: Date | null;
  // Inline editing
  onNameChange?: (name: string) => void;
  // Tags
  workflowTags?: WorkflowTag[];
  availableTags?: Tag[];
  tagsLoading?: boolean;
  onTagAdd?: (tag: Tag) => void;
  onTagRemove?: (tagId: string) => void;
  onTagCreate?: (name: string) => Promise<Tag | null>;
}

export default function WorkflowActions({
  onSave,
  onSaveAs,
  onNew,
  onLoad,
  onExport,
  workflowName,
  workflowId,
  isSaving = false,
  hasUnsavedChanges = false,
  lastSaved,
  onNameChange,
  workflowTags = [],
  availableTags = [],
  tagsLoading = false,
  onTagAdd,
  onTagRemove,
  onTagCreate,
}: WorkflowActionsProps) {
  // Inline name editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(workflowName || '');
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Tag popover state
  const [tagPopoverOpen, setTagPopoverOpen] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const [isCreatingTag, setIsCreatingTag] = useState(false);

  // Sync edited name with prop
  useEffect(() => {
    if (!isEditingName) {
      setEditedName(workflowName || '');
    }
  }, [workflowName, isEditingName]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  const formatLastSaved = (date: Date) => {
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diff < 60) return 'Guardado justo ahora';
    if (diff < 3600) return `Guardado hace ${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `Guardado hace ${Math.floor(diff / 3600)}h`;
    return `Guardado ${date.toLocaleDateString()}`;
  };

  const isExistingWorkflow = Boolean(workflowId);

  // Handle name edit submit
  const handleNameSubmit = useCallback(() => {
    const trimmedName = editedName.trim();
    if (trimmedName && trimmedName !== workflowName && onNameChange) {
      onNameChange(trimmedName);
    }
    setIsEditingName(false);
  }, [editedName, workflowName, onNameChange]);

  // Handle name edit cancel
  const handleNameCancel = useCallback(() => {
    setEditedName(workflowName || '');
    setIsEditingName(false);
  }, [workflowName]);

  // Handle name input keydown
  const handleNameKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleNameSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleNameCancel();
    }
  }, [handleNameSubmit, handleNameCancel]);

  // Filter available tags (exclude already selected)
  const filteredTags = availableTags.filter(
    (tag) => !workflowTags.some((wt) => wt.id === tag.id)
  );

  // Filter by search
  const searchedTags = tagSearch
    ? filteredTags.filter((tag) =>
        tag.name.toLowerCase().includes(tagSearch.toLowerCase())
      )
    : filteredTags;

  // Check if search term matches any existing tag
  const canCreateTag = tagSearch.trim().length > 0 &&
    !availableTags.some((tag) => tag.name.toLowerCase() === tagSearch.toLowerCase().trim());

  // Handle tag selection
  const handleTagSelect = useCallback((tag: Tag) => {
    if (onTagAdd) {
      onTagAdd(tag);
    }
    setTagSearch('');
  }, [onTagAdd]);

  // Handle tag creation
  const handleTagCreate = useCallback(async () => {
    if (!onTagCreate || !tagSearch.trim()) return;

    setIsCreatingTag(true);
    try {
      const newTag = await onTagCreate(tagSearch.trim());
      if (newTag && onTagAdd) {
        onTagAdd(newTag);
      }
      setTagSearch('');
    } catch (error) {
      console.error('Error creating tag:', error);
    } finally {
      setIsCreatingTag(false);
    }
  }, [onTagCreate, onTagAdd, tagSearch]);

  // Calculate visible tags and overflow count (n8n style: max 4 visible)
  const MAX_VISIBLE_TAGS = 4;
  const visibleTags = workflowTags.slice(0, MAX_VISIBLE_TAGS);
  const hiddenTagsCount = workflowTags.length - MAX_VISIBLE_TAGS;
  const hiddenTags = workflowTags.slice(MAX_VISIBLE_TAGS);

  return (
    <Panel position="top-left">
      <TooltipProvider>
        <div className="flex flex-col gap-2">
          {/* Workflow Name + Tags (inline, n8n style) */}
          <div className="flex flex-col gap-1.5 px-3 py-2 bg-background/95 border border-border rounded-md shadow-sm">
            {/* Name + Tags Row */}
            <div className="flex items-center gap-2 flex-wrap">
              {isEditingName ? (
                <div className="flex items-center gap-1">
                  <Input
                    ref={nameInputRef}
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    onKeyDown={handleNameKeyDown}
                    onBlur={handleNameSubmit}
                    className="h-7 text-sm font-semibold w-[160px]"
                    placeholder="Nombre del flujo visual"
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6"
                    onClick={handleNameSubmit}
                  >
                    <Check className="h-3 w-3" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6"
                    onClick={handleNameCancel}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 group shrink-0">
                  <button
                    onClick={() => onNameChange && setIsEditingName(true)}
                    className="flex items-center gap-1 text-left hover:bg-muted/50 rounded px-1 -ml-1 transition-colors"
                    disabled={!onNameChange}
                  >
                    <span className="text-sm font-semibold truncate max-w-[160px]">
                      {workflowName || 'Sin título'}
                    </span>
                    {hasUnsavedChanges && (
                      <span className="text-orange-500 text-lg leading-none">•</span>
                    )}
                    {onNameChange && (
                      <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </button>
                  {isSaving && (
                    <Badge variant="secondary" className="text-xs animate-pulse gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Guardando
                    </Badge>
                  )}
                </div>
              )}

              {/* Tags inline with name (n8n style) */}
              {!isEditingName && (
                <div className="flex items-center gap-1 flex-wrap">
                  {/* Visible Tags (max 4) */}
                  {visibleTags.map((tag) => (
                    <Badge
                      key={tag.id}
                      variant="secondary"
                      className="gap-0.5 pr-1 text-xs h-5"
                      style={tag.color ? { backgroundColor: `${tag.color}20`, borderColor: tag.color } : undefined}
                    >
                      <span className="truncate max-w-[60px]">{tag.name}</span>
                      {onTagRemove && (
                        <button
                          onClick={() => onTagRemove(tag.id)}
                          className="ml-0.5 hover:bg-muted rounded-full p-0.5"
                          aria-label={`Quitar tag ${tag.name}`}
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      )}
                    </Badge>
                  ))}

                  {/* Hidden tags count badge with tooltip */}
                  {hiddenTagsCount > 0 && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge
                          variant="outline"
                          className="text-xs h-5 px-1.5 cursor-default"
                        >
                          +{hiddenTagsCount}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[200px]">
                        <p className="text-xs font-medium mb-1">Tags adicionales:</p>
                        <div className="flex flex-wrap gap-1">
                          {hiddenTags.map((tag) => (
                            <span
                              key={tag.id}
                              className="text-xs px-1.5 py-0.5 rounded"
                              style={tag.color ? { backgroundColor: `${tag.color}30` } : { backgroundColor: 'hsl(var(--muted))' }}
                            >
                              {tag.name}
                            </span>
                          ))}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  )}

                  {/* Add Tag Button (compact) */}
                  {(onTagAdd || onTagCreate) && (
                    <Popover open={tagPopoverOpen} onOpenChange={setTagPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground"
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[220px] p-0" align="start">
                        <Command shouldFilter={false}>
                          <CommandInput
                            placeholder="Buscar o crear tag..."
                            value={tagSearch}
                            onValueChange={setTagSearch}
                          />
                          <CommandList>
                            {tagsLoading ? (
                              <div className="flex items-center justify-center py-4">
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                <span className="text-sm text-muted-foreground">Cargando...</span>
                              </div>
                            ) : (
                              <>
                                {searchedTags.length === 0 && !canCreateTag && (
                                  <CommandEmpty>No se encontraron tags</CommandEmpty>
                                )}
                                {searchedTags.length > 0 && (
                                  <CommandGroup heading="Tags disponibles">
                                    {searchedTags.slice(0, 10).map((tag) => (
                                      <CommandItem
                                        key={tag.id}
                                        value={tag.name}
                                        onSelect={() => handleTagSelect(tag)}
                                      >
                                        <TagIcon className="h-3 w-3 mr-2" style={tag.color ? { color: tag.color } : undefined} />
                                        <span>{tag.name}</span>
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                )}
                                {canCreateTag && onTagCreate && (
                                  <>
                                    {searchedTags.length > 0 && <CommandSeparator />}
                                    <CommandGroup>
                                      <CommandItem
                                        onSelect={handleTagCreate}
                                        disabled={isCreatingTag}
                                        className="text-primary"
                                      >
                                        {isCreatingTag ? (
                                          <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                                        ) : (
                                          <Plus className="h-3 w-3 mr-2" />
                                        )}
                                        <span>Crear "{tagSearch.trim()}"</span>
                                      </CommandItem>
                                    </CommandGroup>
                                  </>
                                )}
                              </>
                            )}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              )}
            </div>

            {/* Status Row */}
            <div className="text-xs text-muted-foreground">
              {lastSaved ? (
                formatLastSaved(lastSaved)
              ) : (
                isExistingWorkflow ? 'Sin cambios pendientes' : 'Nuevo flujo visual'
              )}
            </div>
          </div>

          {/* Action Buttons - Microsoft Word inspired */}
          <div className="flex items-center gap-1">
            {/* Split Button: Guardar + dropdown */}
            <div className="flex shadow rounded-md">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label="Guardar (Ctrl+S)"
                    size="sm"
                    variant={hasUnsavedChanges ? "default" : "outline"}
                    onClick={onSave}
                    disabled={isSaving}
                    className="rounded-r-none border-r-0"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Guardar
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {isExistingWorkflow
                    ? 'Guardar cambios (Ctrl+S)'
                    : 'Guardar nuevo flujo visual (Ctrl+S)'
                  }
                </TooltipContent>
              </Tooltip>

              <DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button
                        aria-label="Más opciones de guardado"
                        size="sm"
                        variant={hasUnsavedChanges ? "default" : "outline"}
                        disabled={isSaving}
                        className="rounded-l-none px-2"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Más opciones</TooltipContent>
                </Tooltip>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuItem onClick={onSave} disabled={isSaving}>
                    <Save className="h-4 w-4 mr-2" />
                    <div className="flex flex-col">
                      <span>Guardar</span>
                      <span className="text-xs text-muted-foreground">Ctrl+S</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onSaveAs} disabled={isSaving}>
                    <SaveAll className="h-4 w-4 mr-2" />
                    <div className="flex flex-col">
                      <span>Guardar como...</span>
                      <span className="text-xs text-muted-foreground">Ctrl+Shift+S</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onNew} disabled={isSaving}>
                    <FilePlus2 className="h-4 w-4 mr-2" />
                    <div className="flex flex-col">
                      <span>Nuevo flujo visual</span>
                      <span className="text-xs text-muted-foreground">Ctrl+N</span>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Open Button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  aria-label="Abrir flujo visual (Ctrl+O)"
                  size="sm"
                  variant="outline"
                  onClick={onLoad}
                  disabled={isSaving}
                  className="shadow"
                >
                  <FolderOpen className="h-4 w-4 mr-2" />
                  Abrir
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                Abrir flujo visual (Ctrl+O)
              </TooltipContent>
            </Tooltip>

            {/* Export Button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  aria-label="Exportar flujo visual"
                  size="sm"
                  variant="outline"
                  onClick={onExport}
                  disabled={isSaving || !isExistingWorkflow}
                  className="shadow"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                Exportar a JSON
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </TooltipProvider>
    </Panel>
  );
}
